<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemReleaseNotes') }}</h1>
        <br />
        <div class="row">
            <!-- Search field -->
            <search-box
                id="searchReleaseNotes"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <b-link class="px-1 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilter') }}
            </b-link>
        </div>
        <!-- Overview Table -->
        <div>
            <b-table
                striped
                hover
                small
                outlined
                bordered
                v-show="dataLoaded"
                @row-clicked="$_onDetails"
                :tbody-tr-class="$_rowClass"
                :items="filteredRecords"
                :fields="authorizedFields"
                sort-icon-left
            >
            </b-table>
        </div>
        <router-links-footer
            v-if="userInfo.isSuper"
            :routerLinks="[{ name: 'releaseNoteDetailNew' }]"
        />
    </b-container>
</template>

<script>
    import { orderBy } from 'lodash';
    import SearchBox from '../../components/SearchBox.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import { nprogress } from '../../services/utilities';

    export default {
        name: 'ReleaseNotesOverview',

        components: {
            'search-box': SearchBox,
            'router-links-footer': RouterLinksFooter
        },

        data: function () {
            return {
                versionField: {
                    key: 'version',
                    label: this.$t('columnNameVersion'),
                    sortable: true,
                    thStyle: {
                        border: 0,
                        width: '200px'
                    }
                },
                releaseDateField: {
                    key: 'releaseDate',
                    label: this.$t('columnNameReleaseDate'),
                    sortable: true,
                    thStyle: { border: 0 }
                },
                records: [],
                searchText: '',
                userInfo: {}
            };
        },

        computed: {
            dataLoaded() {
                return this.records.length > 0;
            },

            authorizedFields() {
                if (this.userInfo.isSuper) {
                    return [this.versionField, this.releaseDateField];
                } else {
                    return [this.releaseDateField];
                }
            },

            filteredRecords() {
                let records = this.records;
                // Filter records
                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = this.records.filter((record) => {
                        return Object.keys(record).some((key) =>
                            record[key].toString().toLowerCase().includes(tokens)
                        );
                    });
                }
                return records;
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;
        },

        async beforeCreate() {
            nprogress.start();
            // Get all the data from the backend
            await this.$store.dispatch('useApiData', {
                name: 'GetReleaseNotesOverview'
            })
                .then((result) => {
                    let records = result.data;

                    const self = this;
                    records.forEach(function (record) {
                        const dateTimeParts = record.releaseDate.split('T');
                        record.releaseDate = `${dateTimeParts[0]}`;
                        record.orderByVersion = self.$_getOrderByVersion(record);
                    });

                    records = orderBy(records, 'orderByVersion', 'desc');
                    this.records = records;
                    nprogress.done();
                });
        },

        methods: {
            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_onDetails(record) {
                // Set searchTerm as route parameter, and redirect to details
                this.$router.push({
                    path: `/dashboard/releaseNote/detail/${record.id}/${this.searchText}`
                });
            },

            $_rowClass() {
                return 'tablerow';
            },

            /**
             * Turn the releasenote version into format aabbcc where aa is marjor-version, bb = minor-version and cc = patch-version
             * @param {object} releaseNote object
             * @returns {string} formatted version
             */
            $_getOrderByVersion: function (releaseNote) {
                const versions = releaseNote.version.split('.');

                const majorVersion = this.$_getFormattedVersion(versions[0]);
                let minorVersion = '00';
                if (versions.length > 1) {
                    minorVersion = this.$_getFormattedVersion(versions[1]);
                }
                let patchVersion = '00';
                if (versions.length > 2) {
                    patchVersion = this.$_getFormattedVersion(versions[2]);
                }

                return `${majorVersion}${minorVersion}${patchVersion}`;
            },

            /**
             * Format the input version
             * @param {string} inputVersion - input version
             * @returns {string} formatted version
             */
            $_getFormattedVersion: function (inputVersion) {
                return inputVersion.length === 1
                    ? `0${inputVersion}`
                    : inputVersion;
            },

            $_clearFilters() {
                this.$_onInput('');
            }
        }
    };
</script>
